<!--
 * @Description: 访客计划
 * @Author: mdb
 * @Date: 2022-07-18 19:37:48
 * @LastEditTime: 2023-03-13 16:18:16
 * @LastEditors: cxh
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        ref="tableRef"
        tableId="DcCosOfbuVisitorProgram"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item :label="t('visitor.status')">
              <a-radio-group v-model:value="queryForm.status" @change="handleQueryChange('search')">
                <a-radio-button v-for="item in planStatusEnumAll" :key="item.value" :value="item.value">{{
                  t(item.lang)
                }}</a-radio-button>
              </a-radio-group>
            </dc-form-item>
            <dc-form-item :label="t('visitor.customerNumber')">
              <a-input
                v-model:value.trim="queryForm.customerNumber"
                :maxlength="100"
                :placeholder="t('common.pleaseInput')"
                allowClear
              />
            </dc-form-item>
            <dc-form-item :label="t('visitor.customerName')">
              <a-input
                v-model:value.trim="queryForm.customerName"
                :maxlength="100"
                :placeholder="t('common.pleaseInput')"
                allowClear
              />
            </dc-form-item>
            <dc-form-item :label="t('visitor.purpose')">
              <ParamCodesSelect
                v-model:value="queryForm.purpose"
                :options="paramCodeData.ofbu_follow_purpose"
                :placeholder="t('common.pleaseSelect')"
                allowClear
              />
            </dc-form-item>
            <dc-form-item :label="t('visitor.title')">
              <a-input
                v-model:value.trim="queryForm.title"
                :maxlength="100"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('common.department')">
              <CustomerAreaCascader type="search" appId="cos_ofbu" v-model:ids="queryForm.deptIdList" />
            </dc-form-item>
            <dc-form-item :label="t('visitor.dutyByName')">
              <UserSelect v-model:value="queryForm.dutyBy" :placeholder="t('common.pleaseSelect')" allowClear />
            </dc-form-item>
            <dc-form-item :label="t('common.searchTime')">
              <a-input-group compact>
                <a-select v-model:value="queryForm.timeType" style="width: 33%">
                  <a-select-option :value="0">{{ t('common.createTime') }}</a-select-option>
                  <a-select-option :value="1">{{ t('visitor.visitortime') }}</a-select-option>
                </a-select>
                <CosRangePicker
                  isOutputBeijingTime
                  v-model:startTime="queryForm.startTime"
                  v-model:endTime="queryForm.endTime"
                  style="width: 67%"
                />
              </a-input-group>
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <a-button v-permission="'ofbu_visitor_add_btn'" type="primary" @click="triggerAddOrEdit()">{{
            t('btn.add')
          }}</a-button>
          <ExportButton v-permission="'ofbu_visitor_export_btn'" appId="cos_ofbu" taskType="408" :search="queryForm">{{
            t('btn.export')
          }}</ExportButton>
        </template>

        <template #bodyCell="{ column, record, text }">
          <template v-if="column.dataIndex === 'status'">
            <dc-status :enums="statusEnum" :value="text" />
          </template>
          <!-- 当状态为《已完成》（status=3）时不可进行其它操作 -->
          <template v-if="column.dataIndex === 'action'">
            <SellerButton
              v-permission="'ofbu_visitor_complete_btn'"
              type="link"
              :workCode="record.dutyBy"
              :disabled="record.status === 3"
              @click="handleVisitComplete(record)"
              >{{ t('btn.completeVisit') }}</SellerButton
            >
            <a-button
              v-permission="'ofbu_visitor_edit_btn'"
              type="link"
              :disabled="record.status === 3"
              @click="triggerAddOrEdit(record.id)"
              >{{ t('btn.edit') }}</a-button
            >
            <a-button
              v-permission="'ofbu_visitor_del_btn'"
              type="link"
              :disabled="record.status === 3"
              @click="handleDelete(record)"
              >{{ t('btn.del') }}</a-button
            >
          </template>
        </template>
      </cos-table>
    </dc-layout-content>
  </dc-layout-main>
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Modal from '@/hooks/modalType'
import { message } from '@dc-platform/dc-components'
import { planStatusEnum, remindTypeEnum } from '../dataConfig'
import { listByParamCodes } from '@/api/common'
import CustomerAreaCascader from '@/components/CustomerAreaCascader/index.vue'
import { visitorProgramList, visitorProgramDelete } from '@/api/overseasSales/followUpManage'
import type { ParamCodeItem } from '@/typings/common'
import type { FbuVisitorProgramParams, FbuVisitorProgramListItem } from '@/typings/api/followUpManage'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const router = useRouter()

// 计划状态枚举
const planStatusEnumAll = [{ label: '全部', lang: 'enum.all', value: undefined }, ...planStatusEnum]
const statusEnum = computed(() =>
  planStatusEnum.map((item) => ({
    name: t(item.lang),
    value: item.value,
    color: item.color
  }))
)
const paramCodeData: {
  [key: string]: ParamCodeItem[]
} = reactive({
  ofbu_follow_purpose: [], // 拜访目的
  ofbu_follow_mode: [] // 拜访方式（跟进方式）
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_ofbu',
    paramCodes: ['ofbu_follow_purpose', 'ofbu_follow_mode']
  }).then((res) => {
    Object.assign(paramCodeData, res.data)
  })
}
onMounted(() => getByParamCodes())

const tableRef = ref()
const queryForm = reactive<FbuVisitorProgramParams>({
  timeType: 0
})

const columns = computed(() => [
  {
    title: t('visitor.title'),
    dataIndex: 'title',
    cosType: 'link',
    path: '/ofbu/visitorProgram/view',
    query: ['id'],
    fixed: 'left',
    width: 200
  },
  { title: t('visitor.customerNumber'), dataIndex: 'customerNumber' },
  { title: t('visitor.customerName'), dataIndex: 'customerName', width: 200 },
  { title: t('visitor.visitorMode'), dataIndex: 'mode', dicData: paramCodeData.ofbu_follow_mode },
  { title: t('visitor.purpose'), dataIndex: 'purpose', dicData: paramCodeData.ofbu_follow_purpose },
  { title: t('visitor.status'), dataIndex: 'status' },
  { title: t('visitor.remindType'), dataIndex: 'remindType', dicData: remindTypeEnum },
  { title: t('visitor.dutyByName'), dataIndex: 'dutyByName' },
  {
    title: t('visitor.visitortime'),
    dataIndex: ['startTime', 'endTime'],
    width: 350,
    cosType: 'timeRange',
    sorter: true
  },
  { title: t('common.createByName'), dataIndex: 'createByName' },
  { title: t('common.createTime'), dataIndex: 'createTime', sorter: true, width: 160 },
  { title: t('common.remark'), dataIndex: 'remark' },
  {
    title: t('common.action'),
    dataIndex: 'action',
    langWidth: {
      zh: 200,
      en: 260
    },
    fixed: 'right'
  }
])
const getList = (queryForm: FbuVisitorProgramParams, callBack: (res: any) => void) => {
  visitorProgramList(queryForm).then((res) => {
    callBack(res)
  })
}
const handleQueryChange = (type: 'search' | 'fetch') => {
  if (type === 'search') {
    tableRef.value?.search()
  } else {
    tableRef.value?.fetch()
  }
}

// 新增编辑函数
const triggerAddOrEdit = (id?: string) => {
  if (id) {
    router.push({ path: '/ofbu/visitorProgram/edit', query: { id } })
  } else {
    router.push('/ofbu/visitorProgram/add')
  }
}
const handleVisitComplete = (row: FbuVisitorProgramListItem) => {
  router.push({ path: '/ofbu/followUpRecord/visitingPlan', query: { planId: row.id } })
}
const handleDelete = (row: FbuVisitorProgramListItem) => {
  Modal.delete({
    name: row.title,
    onOk() {
      visitorProgramDelete(row.id).then(() => {
        message.success(t('tips.delSuccess'))
        tableRef.value?.delFetch()
      })
    }
  })
}
</script>
<script lang="ts">
export default { name: 'DcCosOFbuVisitorProgram' }
</script>

<style scoped></style>
